import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import AddLinkIcon from '@mui/icons-material/AddLink'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import {
  useBatchScrape,
  CRAWL_FAILED_ERROR,
  CRAWL_NOT_STARTED_ERROR,
  urlKey,
  type PageEntry,
} from '../hooks/useBatchScrape'
import { useDiscoverPages } from '../hooks/useDiscoverPages'
import { useOrgDocs } from '@/utils/hooks/orgs'
import { crawlFailureKind, type CrawlFailureKind } from '@/utils/websiteUploadErrors'
import { isBlockedUrl, getBlockedUrlMessage } from '../utils/urlValidation'
import { normalizeUrl } from '../utils/normalizeUrl'
import { COLORS } from '../theme'
import {
  stepHeadingSx,
  stepDescriptionSx,
  outlinedButtonSx,
  warningBoxSx,
  textFieldSx,
  inputSx,
} from '../styles'
import ProgressScreen from '../components/ProgressScreen'
import type { UUID } from 'crypto'
import type { DiscoveredPage } from '../types'

const MAX_URLS = 10

// One page, however it was written: with or without its scheme, www. or a
// trailing slash. Discovery often returns the www. form of an address entered
// without it, and stored URLs lose their trailing slash.
const pageKey = (url: string) => urlKey(url.trim()).replace(/^www\./, '')
const DISCOVERY_PROGRESS_INCREMENT = 90 / 15 // 0→90% over ~15 seconds

// The next step is the document upload, so a page we can't read can go in there.
const SCRAPE_FAILURE_MESSAGES: Record<CrawlFailureKind, string> = {
  blocked:
    'This website blocked us from reading this page — you can upload it as a PDF in the next step',
  notFound: 'We couldn\u2019t find this page — please check the address is correct',
  noText:
    'We couldn\u2019t read any text on this page. It may be that this site uses protections that stop us reading it, or builds its content in a way we can\u2019t read — you can upload it as a PDF in the next step',
  unreachable:
    'We couldn\u2019t reach this page — try again later, or upload it as a PDF in the next step',
  tooLarge:
    'This page is too large for us to read — you can upload it as a PDF in the next step',
  notUseful:
    'We couldn\u2019t use the content from this page — any missing info can be added in the next step',
}

/** Map backend error strings to user-friendly messages.
 *
 * Kept separate from `formatWebsiteUploadError` deliberately: that one appends
 * guidance to the raw backend string, which suits the profile page, while the
 * wizard shows fully curated copy per page. What this had drifted on was the
 * blocked-host case, which fell through to "could not be read" and had users
 * retrying a URL that will never work.
 */
export function formatScrapeError(error?: string): string {
  const FALLBACK =
    'We hit an unexpected problem reading this page — any missing info can be added in the next step'
  if (!error) return FALLBACK
  if (error.includes('Website URL not supported')) return getBlockedUrlMessage()
  // A URL the validator rejected outright — bad shape, private address. Distinct
  // from a blocked host: this one genuinely is not a usable address.
  if (error.includes('Website URL not allowed'))
    return 'This address could not be used — please check it is a valid, publicly reachable URL'
  const kind = crawlFailureKind(error)
  if (kind) return SCRAPE_FAILURE_MESSAGES[kind]
  if (error.includes('already been uploaded'))
    return 'This page has already been uploaded'
  if (error === CRAWL_NOT_STARTED_ERROR)
    return 'We couldn\u2019t start reading this page — please try again'
  if (error === CRAWL_FAILED_ERROR)
    return 'Something went wrong reading this page — any missing info can be added in the next step'
  // Honest about not knowing, rather than blaming the page itself.
  return FALLBACK
}

export default function UploadWebpages() {
  const { orgUuid, maxStepReached, setStaleFrom, setHideNav } =
    useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const batchScrape = useBatchScrape(orgUuid ?? '', getToken)
  const { data: docs, deleteOrgDoc } = useOrgDocs(orgUuid as UUID, token)
  const { discover: discoverPages } = useDiscoverPages()

  const pages = wizardData.pages

  const [urlInput, setUrlInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const pagesChangedRef = useRef(false)

  // ── Discovery state machine ─────────────────────────────────
  // init: waiting for DB docs to load, then check cache
  // discovering: running discovery API with blocking progress screen
  // ready: discovery done (or skipped), show URL editor
  const [phase, setPhase] = useState<'init' | 'discovering' | 'ready'>('init')
  const discoveryAbortedRef = useRef(false)

  // ── Fake progress for discovery screen (0→90% over ~15s) ──
  const [discoveryProgress, setDiscoveryProgress] = useState(0)
  const discoveryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )

  useEffect(() => {
    if (phase !== 'discovering') return
    setDiscoveryProgress(0)
    discoveryIntervalRef.current = setInterval(() => {
      setDiscoveryProgress((prev) => {
        if (prev >= 90) {
          if (discoveryIntervalRef.current)
            clearInterval(discoveryIntervalRef.current)
          return prev
        }
        return prev + DISCOVERY_PROGRESS_INCREMENT
      })
    }, 1000)
    return () => {
      if (discoveryIntervalRef.current)
        clearInterval(discoveryIntervalRef.current)
    }
  }, [phase])

  const websiteDocs = useMemo(
    () => docs?.filter((d) => d.website_url) ?? [],
    [docs],
  )
  const docsLoaded = docs !== undefined
  const orgWebsite = wizardData.orgForm?.website?.trim() ?? ''

  // Failed, found nothing, or found only the homepage all leave the user
  // looking at one row. Without a word from us that reads as the whole site.
  // Worked out from the list rather than at merge, because a return visit skips
  // the merge. Discovery's own result keeps the note off once the user removes
  // pages it did find, counting only pages the merge could have shown.
  const onlyHomepage = useMemo(() => {
    if (!orgWebsite || pages.length === 0) return false
    const homepage = pageKey(orgWebsite)
    const isHomepage = (url: string) => pageKey(url) === homepage
    const discovered =
      normalizeUrl(wizardData.discoveredForWebsite) === normalizeUrl(orgWebsite)
        ? wizardData.discoveredPages
        : []
    return (
      pages.every((p) => isHomepage(p.url)) &&
      discovered.every((p) => isBlockedUrl(p.url) || isHomepage(p.url))
    )
  }, [orgWebsite, pages, wizardData.discoveredPages, wizardData.discoveredForWebsite])

  // Merge discovered pages + DB website docs into wizardData.pages
  const mergePages = useCallback(
    (discovered: DiscoveredPage[]) => {
      const dbPages: PageEntry[] = websiteDocs.map((d) => ({
        url: d.website_url!,
        status: 'ingested' as const,
        docId: d.id,
        dateAdded: d.date_added
          ? new Date(d.date_added).toLocaleDateString('en-NZ', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : undefined,
      }))
      const dbUrls = new Set(dbPages.map((p) => pageKey(p.url)))

      const remainingSlots = Math.max(0, MAX_URLS - dbPages.length)
      const seen = new Set(dbUrls)
      let pendingPages: PageEntry[] = discovered
        .filter((p) => {
          const key = pageKey(p.url)
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        // Filtered at merge rather than at fetch: discovered pages are persisted
        // in wizard_data and localStorage, so blocked entries cached before this
        // shipped would otherwise keep reappearing on every return visit.
        .filter((p) => !isBlockedUrl(p.url))
        .slice(0, remainingSlots)
        .map((p) => ({
          url: p.url,
          title: p.title,
          status: 'pending' as const,
        }))

      // If discovery found nothing, seed the main website URL so the user
      // sees at least their own site queued for scanning.
      if (pendingPages.length === 0 && remainingSlots > 0 && orgWebsite) {
        const url = orgWebsite.startsWith('http')
          ? orgWebsite
          : `https://${orgWebsite}`
        if (!dbUrls.has(pageKey(url))) {
          pendingPages = [{ url, status: 'pending' as const }]
        }
      }

      const merged = [...dbPages, ...pendingPages]

      if (merged.length > 0) {
        setWizardData({ pages: merged })
      }
    },
    [websiteDocs, setWizardData, orgWebsite],
  )

  // Ref so async callbacks always call the latest mergePages
  const mergePagesRef = useRef(mergePages)
  mergePagesRef.current = mergePages

  // Check cache freshness and decide whether to run discovery blocking.
  // Org details fires background discovery as a warm cache. If it completed
  // before we mount, we merge instantly (cache hit). If not, we wait on
  // discovery behind a blocking progress screen so the user can't add URLs
  // while it is in flight. A background call still running is joined, not
  // repeated (see useDiscoverPages).
  //
  // Guard: the effect only runs when phase === 'init'. Every branch below
  // calls setPhase(), so the phase transitions away from 'init' on the very
  // first successful run — no ref needed to prevent double-fire.
  useEffect(() => {
    if (phase !== 'init') return
    if (!docsLoaded) return

    // Return visit with pages already populated. Statuses are reconciled
    // against the DB and the crawl job store by OnboardingUploadWatcher, which
    // sits above this step and is still watching crawls this step started —
    // so a 'processing' page here is a live job, not a leftover to reset.
    if (pages.length > 0) {
      setPhase('ready')
      return
    }

    // No website — merge DB docs only
    if (!orgWebsite || !orgUuid) {
      mergePages([])
      setPhase('ready')
      return
    }

    // Cache hit — the ORG_DETAILS pre-fetch already completed for this website
    if (
      normalizeUrl(wizardData.discoveredForWebsite) === normalizeUrl(orgWebsite)
    ) {
      mergePages(wizardData.discoveredPages)
      setPhase('ready')
      return
    }

    // Cache stale — run discovery blocking
    discoveryAbortedRef.current = false
    setPhase('discovering')
    setHideNav(true)

    const run = async () => {
      try {
        const freshToken = await getToken()
        const discovered = await discoverPages({
          token: freshToken,
          orgUuid: orgUuid!,
          website: orgWebsite,
        })
        if (!discoveryAbortedRef.current) {
          setWizardData({
            discoveredPages: discovered,
            discoveredForWebsite: orgWebsite,
          })
          mergePagesRef.current(discovered)
        }
      } catch {
        if (!discoveryAbortedRef.current) {
          mergePagesRef.current([])
        }
      }
      if (!discoveryAbortedRef.current) {
        setPhase('ready')
        setHideNav(false)
      }
    }
    run()
  }, [phase, docsLoaded])

  // ── Domain splitting ────────────────────────────────────────
  const mainWebsite = wizardData.orgForm?.website ?? ''
  const orgHostname = useMemo(() => {
    try {
      const url = mainWebsite.startsWith('http')
        ? mainWebsite
        : `https://${mainWebsite}`
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
    } catch {
      return ''
    }
  }, [mainWebsite])

  const isOwnDomain = useCallback(
    (url: string): boolean => {
      if (!orgHostname) return true
      try {
        const normalised = url.startsWith('http') ? url : `https://${url}`
        const hostname = new URL(normalised).hostname
          .toLowerCase()
          .replace(/^www\./, '')
        return hostname === orgHostname || hostname.endsWith(`.${orgHostname}`)
      } catch {
        return false
      }
    },
    [orgHostname],
  )

  const ownPages = pages.filter((p) => isOwnDomain(p.url))
  const externalPages = pages.filter((p) => !isOwnDomain(p.url))

  const handleAddUrl = useCallback(() => {
    const trimmed = urlInput.trim()
    if (!trimmed) return

    if (pages.length >= MAX_URLS) {
      setError(`Maximum ${MAX_URLS} URLs allowed`)
      return
    }

    try {
      new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    if (isBlockedUrl(trimmed)) {
      setError(getBlockedUrlMessage())
      return
    }

    const url = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`

    if (pages.some((p) => pageKey(p.url) === pageKey(url))) {
      setError('This URL is already in the list')
      return
    }

    setError(null)
    setUrlInput('')
    pagesChangedRef.current = true
    setWizardData((prev) => ({
      pages: [...prev.pages, { url, status: 'pending' as const }],
    }))
  }, [urlInput, pages, setWizardData])

  const handleRemove = useCallback(
    async (url: string) => {
      const page = pages.find((p) => p.url === url)
      if (!page || page.status === 'processing') return

      if (page.status === 'ingested' && page.docId) {
        try {
          await deleteOrgDoc.mutateAsync({ token, docId: page.docId })
        } catch {
          // Best-effort delete
        }
      }

      pagesChangedRef.current = true
      setWizardData((prev) => ({
        pages: prev.pages.filter((p) => p.url !== url),
      }))
    },
    [pages, deleteOrgDoc, token, setWizardData],
  )

  // Scrape pending pages on Next click — show progress screen during scrape
  const beforeNext = useCallback(async () => {
    if (pagesChangedRef.current && maxStepReached >= STEPS.PROFILE_GENERATING) {
      setStaleFrom((prev) =>
        prev === null
          ? STEPS.PROFILE_GENERATING
          : Math.min(prev, STEPS.PROFILE_GENERATING),
      )
    }
    const pendingUrls = pages
      .filter((p) => p.status === 'pending')
      .map((p) => p.url)
    if (pendingUrls.length === 0) return

    // Enqueue and go. The POST returns as soon as the job is queued; the crawl
    // itself runs for minutes in a worker, and OnboardingUploadWatcher follows
    // it from above this step. The job id lands on each page here, and the
    // persist that goNext runs straight after this writes it to the server —
    // so a reload or a different device can pick the same crawl back up.
    //
    // Never returns false: a crawl whose outcome we don't know yet is not a
    // reason to trap the user on the step.
    let updatedPages = [...pages]
    await batchScrape.submit(pendingUrls, (updater) => {
      updatedPages =
        typeof updater === 'function' ? updater(updatedPages) : updater
      setWizardData({ pages: updatedPages })
    })
  }, [pages, batchScrape, setWizardData, maxStepReached, setStaleFrom])

  useStep({
    isValid: !batchScrape.isSubmitting,
    beforeNext,
  })

  useEffect(() => {
    return () => {
      discoveryAbortedRef.current = true
    }
  }, [])

  const hasDiscoveredPages = wizardData.discoveredPages.length > 1

  // ── Init: waiting for DB docs to load ─────────────────────────
  if (phase === 'init') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  // ── Discovery progress screen ─────────────────────────────────
  if (phase === 'discovering') {
    return (
      <ProgressScreen
        statusText="Finding pages on your website..."
        helpTexts={[
          "We're scanning your website to find relevant pages. This usually takes a few seconds.",
        ]}
        progress={discoveryProgress}
      />
    )
  }

  // ── Input view ───────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        Your organisation's webpages
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        {hasDiscoveredPages
          ? "We've found pages from your website. Review the list below — remove any that aren't relevant and add any we missed."
          : 'Add webpages from your website that you would like us to review.'}
      </Typography>
      {onlyHomepage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We could only find your homepage. If your website has other pages
          about your organisation, add them below.
        </Alert>
      )}
      {!hasDiscoveredPages && pages.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            ...warningBoxSx,
            mb: 2,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: COLORS.warningIcon,
              fontSize: 22,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
          >
            Don&apos;t have a website? No worries — you can skip this step.
          </Typography>
        </Box>
      )}

      {/* Page list */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: pages.length >= MAX_URLS ? COLORS.error : COLORS.muted,
            mb: 1,
            display: 'block',
          }}
        >
          {pages.length} / {MAX_URLS} URLs
        </Typography>

        {ownPages.length > 0 && (
          <>
            {externalPages.length > 0 && (
              <Typography
                variant="subtitle2"
                sx={{ color: COLORS.primary, fontWeight: 600, mt: 1, mb: 0.5 }}
              >
                Your website pages
              </Typography>
            )}
            <List disablePadding>
              {ownPages.map((page) => (
                <PageItem
                  key={page.url}
                  page={page}
                  isMainSite={page.url === mainWebsite}
                  onRemove={handleRemove}
                />
              ))}
            </List>
          </>
        )}

        {externalPages.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              sx={{ color: COLORS.primary, fontWeight: 600, mt: 2, mb: 0.5 }}
            >
              External pages
            </Typography>
            <List disablePadding>
              {externalPages.map((page) => (
                <PageItem
                  key={page.url}
                  page={page}
                  isMainSite={false}
                  onRemove={handleRemove}
                />
              ))}
            </List>
          </>
        )}

        {pages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: COLORS.secondary }}>
            <Typography variant="body2">
              No webpages added yet. Add URLs below to include website content
              in your profile.
            </Typography>
          </Box>
        )}
      </Box>

      {/* URL input */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mt: 2,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Add a URL (e.g. https://example.org/about)"
          value={urlInput}
          onChange={(e) => {
            setUrlInput(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddUrl()
          }}
          disabled={pages.length >= MAX_URLS}
          variant="outlined"
          sx={{ ...textFieldSx, flexGrow: 1, minWidth: 0 }}
          slotProps={{ input: { sx: inputSx } }}
        />
        <Button
          disableRipple
          variant="outlined"
          startIcon={<AddLinkIcon />}
          onClick={handleAddUrl}
          disabled={!urlInput.trim() || pages.length >= MAX_URLS}
          sx={{ ...outlinedButtonSx, minWidth: 'auto', flexShrink: 0 }}
        >
          Upload
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!hasDiscoveredPages && pages.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: COLORS.muted, mt: 2, textAlign: 'center' }}
        >
          No webpages to add? Click Continue to skip this step.
        </Typography>
      )}
    </Box>
  )
}

// ── Page list item ──────────────────────────────────────────

function PageItem({
  page,
  isMainSite,
  onRemove,
}: {
  page: PageEntry
  isMainSite: boolean
  onRemove: (url: string) => void
}) {
  const secondaryText = isMainSite
    ? 'Main website'
    : page.status === 'error'
      ? formatScrapeError(page.error)
      : page.status === 'ingested'
        ? page.dateAdded
          ? `Scanned on ${page.dateAdded}`
          : 'Scanned'
        : page.status === 'processing'
          ? 'Reading this page — you can carry on'
          : 'Ready to scan'

  return (
    <ListItem
      secondaryAction={
        page.status !== 'processing' ? (
          <IconButton
            edge="end"
            onClick={() => onRemove(page.url)}
            aria-label={`Remove ${page.url}`}
            size="small"
          >
            <DeleteOutlineIcon />
          </IconButton>
        ) : undefined
      }
      sx={{ borderBottom: `1px solid ${COLORS.border}`, py: 1 }}
    >
      <Box
        sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}
        aria-hidden="true"
      >
        <StatusIcon status={page.status} />
      </Box>
      <ListItemText
        // Lead with the page's own title where discovery found one: a bare URL
        // like /accounts/meta_verified gives the user nothing to judge by.
        primary={page.title || page.url}
        secondary={
          <>
            {page.title && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {page.url}
              </Box>
            )}
            {/* The reason wraps onto its own line. Ellipsising it behind the URL
                hid exactly the messages the user needs — a blocked host is two
                sentences, and at dialog width none of it survived. */}
            <Box component="span" sx={{ display: 'block' }}>
              {secondaryText}
            </Box>
          </>
        }
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        secondaryTypographyProps={{ component: 'div' }}
      />
    </ListItem>
  )
}

function StatusIcon({ status }: { status: PageEntry['status'] }) {
  switch (status) {
    case 'ingested':
      return (
        <CheckCircleOutlineIcon sx={{ color: COLORS.success, fontSize: 20 }} />
      )
    case 'processing':
      return <CircularProgress size={18} sx={{ color: COLORS.primary }} />
    case 'error':
      return <ErrorOutlineIcon sx={{ color: COLORS.error, fontSize: 20 }} />
    default:
      return (
        <ScheduleOutlinedIcon
          sx={{
            color: COLORS.lightGray,
            fontSize: 18,
            opacity: 0.75,
          }}
        />
      )
  }
}
